import { Component } from '@theme/component';

/**
 * @typedef {Object} ContentTabsRefs
 * @property {HTMLElement} tablist
 * @property {HTMLElement[]} panels
 */

/** @extends {Component<ContentTabsRefs>} */
class ContentTabsComponent extends Component {
  requiredRefs = ['tablist'];
  #active = 0;
  /** @type {HTMLButtonElement[]} */
  #tabs = [];
  /** @type {AbortController | null} */
  #abort = null;

  connectedCallback() {
    super.connectedCallback();
    if (!this.refs.panels?.length) return;

    this.#buildTabs();
    this.#updateActiveTab();
    this.#setupEventListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
  }

  /**
   * Builds the tab bar from the rendered panels. Each panel carries its label in
   * `data-tab-label`, so the buttons are generated here rather than moving DOM.
   */
  #buildTabs() {
    const { tablist, panels } = this.refs;
    if (!tablist || !panels) return;

    tablist.replaceChildren();
    this.#tabs = panels.map((panel, index) => {
      const tabId = panel.id.replace(/^Panel-/, 'Tab-');
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'content-tabs__tab';
      button.id = tabId;
      button.setAttribute('role', 'tab');
      button.setAttribute('aria-controls', panel.id);
      button.setAttribute('aria-selected', 'false');
      button.setAttribute('tabindex', '-1');
      button.textContent = panel.dataset.tabLabel || `Tab ${index + 1}`;
      panel.setAttribute('aria-labelledby', tabId);
      tablist.appendChild(button);
      return button;
    });
  }

  #setupEventListeners() {
    this.#abort?.abort();
    this.#abort = new AbortController();
    const opts = { signal: this.#abort.signal };

    this.addEventListener('keydown', (event) => this.#handleKeydown(event), opts);

    for (const [index, tab] of this.#tabs.entries()) {
      tab.addEventListener('click', () => this.#activate(index), opts);
    }
  }

  /** @param {KeyboardEvent} event */
  #handleKeydown(event) {
    const target = /** @type {HTMLElement} */ (event.target);
    if (target.getAttribute('role') !== 'tab') return;

    const index = this.#tabs.indexOf(/** @type {HTMLButtonElement} */ (target));
    if (index === -1) return;

    const navMap = {
      ArrowLeft: -1,
      ArrowRight: 1,
      Home: -index,
      End: this.#tabs.length - 1 - index,
    };

    const offset = navMap[/** @type {keyof typeof navMap} */ (event.key)];
    if (offset === undefined) return;

    event.preventDefault();
    const nextIndex = (index + offset + this.#tabs.length) % this.#tabs.length;
    this.#tabs[nextIndex]?.focus();
    this.#activate(nextIndex);
  }

  /** @param {number} index */
  #activate(index) {
    if (index === this.#active || index < 0 || index >= this.#tabs.length) return;

    this.#active = index;
    this.#updateActiveTab();
  }

  #updateActiveTab() {
    const { panels } = this.refs;

    for (const [index, tab] of this.#tabs.entries()) {
      const isActive = index === this.#active;
      tab.setAttribute('aria-selected', String(isActive));
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
    }

    for (const [index, panel] of panels?.entries() ?? []) {
      const isActive = index === this.#active;
      panel.toggleAttribute('hidden', !isActive);
      panel.toggleAttribute('inert', !isActive);
    }
  }
}

customElements.define('content-tabs-component', ContentTabsComponent);
